import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { rem } from 'polished';
import React from 'react';
import { colorSets, ComponentColorMode, ComponentColors, ComponentSize, getComponentColors } from '../../foundations';
import { borderRadiusScaled } from '../../foundations/borders/borderRadius';
import { elevationShadow } from '../../foundations/elevation/elevation';
import { spacing, spacingScaled } from '../../foundations/spacing/spacing';
import { IndicatorSelected } from '../Indicator/IndicatorSelected/IndicatorSelected';

const indicatorClassName = 'card-indicator';

export type CardProps = {
  cardTheme?: ComponentColorMode;
  active?: boolean;
  size?: ComponentSize;
  resizing?: 'hug' | 'fixed';
  width?: number;
  height?: number;
  autoHeight?: boolean;
  showBorder?: boolean;
  showBoxShadow?: boolean;
  ariaLabel?: string;
  onClick?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onMouseDown?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  className?: string;
  children?: React.ReactNode;
  as?: React.ElementType;
};

export const Card: React.FC<CardProps> = ({
  cardTheme = 'neutral',
  size = 'small',
  resizing = 'hug',
  width,
  height,
  autoHeight,
  active = false,
  showBorder = true,
  showBoxShadow = false,
  ariaLabel,
  onClick,
  onMouseDown,
  className,
  children,
  as,
  ...rest
}) => {
  const colors = getComponentColors(cardTheme, { active });

  // WIP The indicator is now a part of the card component to show the active state, but this might change as we research more use cases.
  return (
    <StyledCard
      colors={colors}
      size={size}
      resizing={resizing}
      $width={width}
      height={height}
      autoHeight={autoHeight}
      showBorder={showBorder}
      borderSize={spacing.size2}
      showBoxShadow={showBoxShadow}
      onClick={onClick}
      onMouseDown={onMouseDown}
      as={as ?? (onClick || onMouseDown ? 'button' : undefined)}
      aria-label={ariaLabel}
      className={className}
      active={active}
      cardTheme={cardTheme}
      {...rest}
    >
      {cardTheme === 'neutral' && (
        <IndicatorSelected
          size={size}
          type="chip"
          verticalOffset={spacing.size8}
          leftOffset={showBorder ? spacing.size2 : undefined}
          className={indicatorClassName}
        />
      )}

      {children}
    </StyledCard>
  );
};

interface StyledCardProps {
  size: ComponentSize;
  resizing: 'hug' | 'fixed';
  active?: boolean;
  cardTheme?: ComponentColorMode;
  $width?: number;
  height?: number;
  colors: ComponentColors;
  showBorder: boolean;
  borderSize: spacing;
  showBoxShadow: boolean;
}

const StyledCard = styled.div<StyledCardProps & { autoHeight?: boolean }>`
  display: ${({ resizing }) => (resizing === 'hug' ? 'inline-block' : 'block')};
  text-align: left;
  position: relative;
  border-radius: ${({ size }) => borderRadiusScaled(spacing.size8, size)};
  border: ${({ showBorder, borderSize, colors, size }) =>
    showBorder ? `${rem(spacingScaled(borderSize, size))} solid ${colors.stroke}` : undefined};
  background: ${({ colors }) => colors.fill};
  width: ${({ resizing, $width }) => (resizing === 'hug' ? 'fit-content' : $width ? rem($width) : '100%')};
  height: ${({ resizing, height, autoHeight }) =>
    autoHeight ? 'auto' : resizing === 'hug' ? 'fit-content' : height ? rem(height) : '100%'};
  color: ${({ colors }) => colors.on};
  box-shadow: ${({ showBoxShadow }) => (showBoxShadow ? elevationShadow.e100 : undefined)};
  white-space: pre-wrap;
  word-break: break-word;

  .${indicatorClassName} {
    opacity: ${({ active }) => (active ? 1 : 0)};
  }

  ${({ onClick, cardTheme }) =>
    !!onClick &&
    cardTheme === 'neutral' &&
    css`
      &:hover {
        cursor: pointer;
        background: ${colorSets.active.fill};
        color: ${colorSets.active.on};
        border-color: ${colorSets.active.stroke};

        .${indicatorClassName} {
          opacity: 1;
        }
      }
    `}
`;
